import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import {
  getAccessToken,
  getRefreshToken,
  getSession,
  saveSession,
  saveTokens,
  clearSession,
  setActiveBusinessId,
  ACCESS_KEY,
} from "./tokenStore";
import { can, roleOf } from "./permissions";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

/* The login / switch-business responses share one shape. */
function readSession(payload) {
  const u = payload.user || {};
  return {
    user: {
      id: u.id,
      userName: u.userName,
      full_name: u.full_name,
      role: u.role || u.access,
      // Effective permission set for the active business (role defaults or
      // an admin-pinned override). Absent on pre-override sessions.
      permissions: Array.isArray(u.permissions) ? u.permissions : undefined,
      access: u.access,
    },
    business: payload.business || null,
    memberships: payload.memberships || [],
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);

  const applySession = useCallback((payload) => {
    const session = readSession(payload);
    saveSession(session);
    setUser(session.user);
    setBusiness(session.business);
    setMemberships(session.memberships);
    return session;
  }, []);

  // On mount, verify the stored session. /auth/verify only echoes the token
  // payload, so the business/membership detail is rehydrated from the copy
  // saved at login (or at the last business switch).
  useEffect(() => {
    let cancelled = false;
    async function verify() {
      setLoading(true);
      if (!getAccessToken() && !getRefreshToken()) {
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }
      // A stale access token 401s here; the axios interceptor refreshes and
      // replays the call, so an idle-overnight reload still lands signed in.
      try {
        const res = await axios.get(`${BASE_SERVER_URL}/auth/verify`);
        if (cancelled) return;
        if (res?.data?.success) {
          const claims = res.data.data.user || {};
          const stored = getSession() || {};
          setUser({
            id: claims.sub || claims.id,
            userName: claims.userName,
            full_name: claims.full_name,
            role: claims.role || claims.access,
            permissions: Array.isArray(claims.permissions) ? claims.permissions : undefined,
            access: claims.access,
          });
          // Trust the token's business over the stored copy: a refresh or a
          // switch in another tab can move the session to another business.
          const fromMemberships = (stored.memberships || []).find(
            (m) => String(m.business_id) === String(claims.business_id)
          );
          setBusiness(
            fromMemberships
              ? {
                  id: fromMemberships.business_id,
                  name: fromMemberships.business_name,
                  code: fromMemberships.business_code,
                }
              : stored.business || null
          );
          setMemberships(stored.memberships || []);
        } else {
          setUser(null);
        }
      } catch (_err) {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    verify();

    // React to token changes from other tabs/windows
    const onStorage = (e) => {
      if (e.key === ACCESS_KEY && !e.newValue) {
        setUser(null);
      }
    };
    // Dispatched by axiosConfig when the session is over.
    const onUnauthorized = () => {
      setUser(null);
      setBusiness(null);
      setMemberships([]);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("rf:unauthorized", onUnauthorized);
    return () => {
      cancelled = true;
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("rf:unauthorized", onUnauthorized);
    };
  }, []);

  const login = async ({ userName, password }) => {
    // Server rejects non-strings with 400 — coerce before sending.
    const res = await axios.post(
      `${BASE_SERVER_URL}/auth/login`,
      { userName: String(userName ?? ""), password: String(password ?? "") },
      { withCredentials: true }
    );

    const data = res?.data?.data;
    if (res?.data?.success && data?.access_token) {
      saveTokens(data.access_token, data.refresh_token);
      applySession(data);
      return { ok: true };
    }
    return { ok: false, error: "Invalid credentials" };
  };

  const logout = async () => {
    const refresh_token = getRefreshToken();
    try {
      // Revoke server-side first — once the tokens are cleared locally the
      // request can no longer identify the session.
      await axios.post(
        `${BASE_SERVER_URL}/auth/logout`,
        { refresh_token },
        { withCredentials: true }
      );
    } catch (_err) {
      // no-op; we still proceed with local logout
    } finally {
      clearSession();
      setUser(null);
      setBusiness(null);
      setMemberships([]);
    }
  };

  /* Switching issues a fresh token pair bound to the other business. Every
     list on screen belongs to the old one, so callers must refetch — the
     simplest correct thing is a full reload. */
  const switchBusiness = async (business_id) => {
    const res = await axios.post(
      `${BASE_SERVER_URL}/auth/switch-business`,
      { business_id: String(business_id) },
      { withCredentials: true }
    );
    const data = res?.data?.data;
    if (!res?.data?.success || !data?.access_token) {
      return { ok: false, error: res?.data?.message || "Could not switch business" };
    }
    saveTokens(data.access_token, data.refresh_token);
    // The switcher only exists inside a business (AppShell sidebar), so the
    // active business the `business-id` header carries moves with the session.
    setActiveBusinessId(business_id);
    applySession(data);
    return { ok: true };
  };

  const value = useMemo(
    () => ({
      user,
      setUser,
      business,
      memberships,
      loading,
      login,
      logout,
      switchBusiness,
      role: roleOf(user),
      can: (permission) => can(user, permission),
    }),
    [user, business, memberships, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
